import React, { ComponentType, useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import tenants from '~/consts/tenant';

import AffiliationList from '../LeAgentHub/components/Affiliation/AffiliationList';
import AgencyList from '../LeAgentHub/components/Agency/AgencyList';
import AgencySubmissions from '../LeAgentHub/components/AgencySubmissions/AgencySubmissions';

export const TAB_AGENCY_SUBMISSION = 'agency-submission';
export const TAB_AGENCIES = 'agencies';
export const TAB_AFFILIATIONS = 'affiliations';

type TabKey = typeof TAB_AGENCY_SUBMISSION | typeof TAB_AGENCIES | typeof TAB_AFFILIATIONS;

type UsersPageContainerProps = {
  allowedTabs?: Array<TabKey>;
  basePath?: string;
  pageTitlePrefix?: string;
} & Record<string, unknown>;

type Tab = {
  label: string;
  pageTitle: string;
  component: ComponentType;
  tenants?: Set<string>;
  showTab: boolean;
};

export default function UsersPageContainer({
  allowedTabs,
  basePath = '/users-list',
  pageTitlePrefix = 'Users',
}: UsersPageContainerProps = {}) {
  const TABS_MAP = {
    [TAB_AGENCY_SUBMISSION]: {
      label: 'Agency Submission',
      pageTitle: 'Agency Submission',
      component: AgencySubmissions,
      tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
      showTab: true,
    } as Tab,
    [TAB_AGENCIES]: {
      label: 'Agencies',
      pageTitle: 'Agencies',
      component: AgencyList,
      tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
      showTab: true,
    } as Tab,
    [TAB_AFFILIATIONS]: {
      label: 'Affiliations',
      pageTitle: 'Affiliations',
      component: AffiliationList,
      tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
      showTab: true,
    },
  } as const;

  type MapTabKey = keyof typeof TABS_MAP;

  const permittedTabKeys = useMemo(() => {
    if (!allowedTabs?.length) {
      return Object.keys(TABS_MAP) as Array<MapTabKey>;
    }

    return allowedTabs.filter((key): key is MapTabKey => key in TABS_MAP);
  }, [allowedTabs]);

  const { tab } = useParams<{ tab?: MapTabKey }>();
  const { pathname } = useLocation();
  const pathTab = pathname.replace(`${basePath}/`, '').split('/')[0];
  const currentPathTab = tab ?? pathTab;

  const brand = useSelector((state: App.State) => state.tenant);

  const allTabs = useMemo(() => {
    return permittedTabKeys.filter(
      (key) => TABS_MAP[key].showTab && (!TABS_MAP[key].tenants || TABS_MAP[key].tenants.has(brand.value)),
    );
  }, [brand, permittedTabKeys]);

  const resolvedTab = allTabs.includes(currentPathTab as MapTabKey) ? (currentPathTab as MapTabKey) : allTabs[0];
  const currentTab = resolvedTab ? TABS_MAP[resolvedTab] : undefined;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{`${pageTitlePrefix} / ${currentTab?.label ?? ''}`}</title>
      </Helmet>

      {currentTab && <PageHeader title={currentTab.pageTitle} />}

      {currentTab && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={resolvedTab}>
              {allTabs.map((tabKey) => (
                <Tab
                  key={tabKey}
                  value={tabKey}
                  label={TABS_MAP[tabKey].label}
                  to={`${basePath}/${tabKey}`}
                  component={Link}
                />
              ))}
            </Tabs>
          </Box>

          <Box pt={4}>
            <Box component={currentTab.component} />
          </Box>
        </>
      )}
    </Container>
  );
}
